import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { AsyncLoadResult, AsyncLoadOptions } from '../../../../shared/abstractStore';
import { editQuestionnaireSupportingDataQuery } from '../../generated/editQuestionnaireSupportingDataQuery';
import { mainApiConfig } from '../../../../configure/mainApiConfig';
import { subscriptionDepartmentFieldsFragment } from '../../models/SubscriptionDepartment';
import { subscriptionFieldsFragment } from '../../models/Subscription';
import { analysisTypeFieldsFragment } from '../../models/AnalysisType';

export interface EditQuestionnaireSupportingDataAsyncLoadOptions extends AsyncLoadOptions {
    subscriptionId?: string | null | undefined,
}

/**
 * Supporting data for EditQuestion that loads static data (e.g. dropdowns) that are not changed by the model being used.
 * @param options
 */
export function useEditQuestionnaireSupportingData(options: EditQuestionnaireSupportingDataAsyncLoadOptions = {}): AsyncLoadResult<editQuestionnaireSupportingDataQuery> {
    // Query the data we need form the api.
    const [load, { data, loading, error, refetch }] = useLazyQuery<editQuestionnaireSupportingDataQuery>(
        gql`
        query editQuestionnaireSupportingDataQuery ($subscriptionId: ID!) {
            subscription: subscription(id: $subscriptionId){
                ...subscriptionFields
            }

            subscriptionDepartments: subscriptionDepartments(subscriptionId: $subscriptionId) {
                ...subscriptionDepartmentFields
            }

            analysisTypes: analysisTypes(subscriptionId: $subscriptionId) {
                ...analysisTypeFields
            }
        }

        ${subscriptionFieldsFragment}
        ${subscriptionDepartmentFieldsFragment}
        ${analysisTypeFieldsFragment}
        `,
        {
            variables: {
                subscriptionId: options.subscriptionId,
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error
    }), [data, refresh, loading, error]);
}